import { useState, useEffect, useCallback } from 'react';
import usePerimetre from '../hooks/usePerimetre';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import NotificationBell from '../components/NotificationBell';

const API = process.env.REACT_APP_API_URL || '';

const MOIS_NOMS = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const COLS = [
  { key: 'h_normales',     label: 'H.Norm',  color: '#1B5E20' },
  { key: 'h_hs_15',        label: 'HS 15%',  color: '#F57F17' },
  { key: 'h_hs_40',        label: 'HS 40%',  color: '#BF360C' },
  { key: 'h_dimanche',     label: 'Dim',     color: '#1565C0' },
  { key: 'h_nuit_normal',  label: 'Nuit',    color: '#0D47A1' },
  { key: 'nb_paniers',     label: 'Paniers', color: '#558B2F' },
  { key: 'h_absent_just',  label: 'Abs.J',   color: '#FF8F00' },
  { key: 'h_absent_injust','label': 'Abs.IJ', color: '#C62828' },
];

function fmt2(val) {
  const n = parseFloat(val || 0);
  return n === 0 ? '—' : n.toFixed(2);
}

function Skeleton({ h = 14 }) {
  return (
    <div style={{
      height: h, background: 'linear-gradient(90deg,#e0e0e0 25%,#f5f5f5 50%,#e0e0e0 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', borderRadius: 4,
    }} />
  );
}

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24,
      background: type === 'success' ? '#1B5E20' : '#C62828',
      color: '#fff', padding: '12px 20px', borderRadius: 10, zIndex: 9999, fontSize: 14,
      boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
    }}>{msg}</div>
  );
}

export default function RecapMensuelPage() {
  const { employe, token } = useAuth();
  const today = new Date();
  const [annee, setAnnee] = useState(today.getFullYear());
  const [mois, setMois]   = useState(today.getMonth() + 1);
  const [siteId, setSiteId] = useState('');
  // Module 0 (clôture) : sites du périmètre réel uniquement.
  const { perimetre, sites } = usePerimetre('peut_voir_temps_site');
  useEffect(() => {
    if (perimetre && !perimetre.global && sites.length === 1) setSiteId(sites[0].id);
  }, [perimetre, sites]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);


  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ annee, mois });
      if (siteId) params.append('site_id', siteId);
      const { data: d } = await axios.get(`${API}/api/temps/recap/mensuel?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(d);
    } catch { setData(null); } finally { setLoading(false); }
  }, [annee, mois, siteId, token]);

  useEffect(() => { charger(); }, [charger]);

  const handleExport = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recap_mensuel_${annee}_${String(mois).padStart(2,'0')}.json`;
    a.click();
    setToast({ msg: 'Export téléchargé', type: 'success' });
  };

  const parSemaine = data?.par_semaine || [];
  const parEmploye = data?.par_employe || [];
  const semCloturees = parSemaine.filter(s => s.statut === 'cloture').length;
  const pctClotures = parSemaine.length > 0 ? Math.round((semCloturees / parSemaine.length) * 100) : 0;

  const totauxSem = parSemaine.reduce((acc, s) => {
    COLS.forEach(c => { acc[c.key] = (acc[c.key] || 0) + parseFloat(s[c.key] || 0); });
    return acc;
  }, {});
  const totauxEmp = parEmploye.reduce((acc, e) => {
    COLS.forEach(c => { acc[c.key] = (acc[c.key] || 0) + parseFloat(e[c.key] || 0); });
    return acc;
  }, {});

  const annees = Array.from({ length: 5 }, (_, i) => today.getFullYear() - 2 + i);

  return (
    <Layout>
      <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}} @media(max-width:768px){.t-scroll{overflow-x:auto;}}`}</style>

      {/* Header */}
      <div className="sg-bandeau-clair" style={{
        background: '#1B5E20', color: '#fff', borderRadius: '14px 14px 0 0',
        padding: '14px 20px', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
      }}>
        <h2 style={{ margin: 0, fontSize: 20, flex: 1 }}>Récapitulatif Mensuel</h2>
        <NotificationBell couleurIcone="#fff" taille={17} />
        <select value={mois} onChange={e => setMois(+e.target.value)}
          style={{ padding: '6px 12px', borderRadius: 8, border: 'none', fontSize: 13 }}>
          {MOIS_NOMS.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
        </select>
        <select value={annee} onChange={e => setAnnee(+e.target.value)}
          style={{ padding: '6px 12px', borderRadius: 8, border: 'none', fontSize: 13 }}>
          {annees.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        {sites.length > 1 && (
          <select value={siteId} onChange={e => setSiteId(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 8, border: 'none', fontSize: 13 }}>
            <option value="">{perimetre?.global ? 'Tous les sites' : 'Tous mes sites'}</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        )}
      </div>

      <div style={{ background: '#fff', borderRadius: '0 0 14px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>

        {/* ─── Indicateur clôture ─── */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Clôture des semaines</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: pctClotures === 100 ? '#1B5E20' : '#F57F17' }}>
              {pctClotures}% ({semCloturees}/{parSemaine.length})
            </div>
          </div>
          <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4, height: 8, minWidth: 100 }}>
            <div style={{ width: `${pctClotures}%`, background: pctClotures === 100 ? '#1B5E20' : '#F57F17', height: '100%', borderRadius: 4, transition: 'width 0.5s' }} />
          </div>
          <button onClick={handleExport} style={{ background: '#1B5E20', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            Exporter le mois
          </button>
        </div>

        {/* ─── Tableau 1 : Par semaine ─── */}
        <div style={{ padding: '16px 20px' }}>
          <h3 style={{ color: '#1B5E20', margin: '0 0 12px', fontSize: 15 }}>Vue par semaine</h3>
          <div className="t-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ background: '#1B5E20', color: '#fff' }}>
                  <th style={thS}>Semaine</th>
                  <th style={thS}>Statut</th>
                  {COLS.map(c => <th key={c.key} style={thS}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {loading ? Array(4).fill(0).map((_, i) => (
                  <tr key={i}>{Array(COLS.length + 2).fill(0).map((_, j) => (
                    <td key={j} style={{ padding: 10 }}><Skeleton /></td>
                  ))}</tr>
                )) : parSemaine.length === 0 ? (
                  <tr><td colSpan={COLS.length + 2} style={{ textAlign: 'center', padding: 30, color: '#999' }}>Aucune donnée</td></tr>
                ) : (
                  parSemaine.map(s => (
                    <tr key={s.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={tdS}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {new Date(s.date_debut).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                          {' → '}
                          {new Date(s.date_fin).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </div>
                      </td>
                      <td style={tdS}>
                        <span style={{
                          background: s.statut === 'cloture' ? '#E8F5E9' : '#FFF8E1',
                          color: s.statut === 'cloture' ? '#1B5E20' : '#F57F17',
                          borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700,
                        }}>
                          {s.statut === 'cloture' ? 'Clôturée' : 'Ouverte'}
                        </span>
                      </td>
                      {COLS.map(c => (
                        <td key={c.key} style={{ ...tdS, textAlign: 'center', color: parseFloat(s[c.key] || 0) > 0 ? c.color : '#ccc', fontWeight: parseFloat(s[c.key] || 0) > 0 ? 700 : 400 }}>
                          {fmt2(s[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
              {!loading && parSemaine.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#E8F5E9', fontWeight: 700 }}>
                    <td style={tdS} colSpan={2}>Total</td>
                    {COLS.map(c => <td key={c.key} style={{ ...tdS, textAlign: 'center', color: '#1B5E20' }}>{fmt2(totauxSem[c.key])}</td>)}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* ─── Tableau 2 : Par employé ─── */}
        <div style={{ padding: '0 20px 20px' }}>
          <h3 style={{ color: '#1B5E20', margin: '0 0 12px', fontSize: 15 }}>Vue par employé</h3>
          <div className="t-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
              <thead>
                <tr style={{ background: '#1B5E20', color: '#fff' }}>
                  <th style={thS}>Employé</th>
                  {COLS.map(c => <th key={c.key} style={thS}>{c.label}</th>)}
                  <th style={thS}>Statut</th>
                </tr>
              </thead>
              <tbody>
                {loading ? Array(5).fill(0).map((_, i) => (
                  <tr key={i}>{Array(COLS.length + 2).fill(0).map((_, j) => (
                    <td key={j} style={{ padding: 10 }}><Skeleton /></td>
                  ))}</tr>
                )) : parEmploye.length === 0 ? (
                  <tr><td colSpan={COLS.length + 2} style={{ textAlign: 'center', padding: 30, color: '#999' }}>Aucune donnée</td></tr>
                ) : (
                  parEmploye.map(e => (
                    <tr key={e.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={tdS}>
                        <div style={{ fontWeight: 600 }}>{e.prenom} {e.nom}</div>
                        <div style={{ fontSize: 11, color: '#888' }}>{e.matricule}</div>
                      </td>
                      {COLS.map(c => (
                        <td key={c.key} style={{ ...tdS, textAlign: 'center', color: parseFloat(e[c.key] || 0) > 0 ? c.color : '#ccc', fontWeight: parseFloat(e[c.key] || 0) > 0 ? 700 : 400 }}>
                          {fmt2(e[c.key])}
                        </td>
                      ))}
                      <td style={{ ...tdS, textAlign: 'center' }}>
                        {e.tout_cloture
                          ? <span style={{ color: '#1B5E20', fontWeight: 700, fontSize: 12 }}>Tout clôturé</span>
                          : <span style={{ color: '#F57F17', fontWeight: 700, fontSize: 12 }}>En cours</span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {!loading && parEmploye.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#E8F5E9', fontWeight: 700 }}>
                    <td style={tdS}>Total ({parEmploye.length} employés)</td>
                    {COLS.map(c => <td key={c.key} style={{ ...tdS, textAlign: 'center', color: '#1B5E20' }}>{fmt2(totauxEmp[c.key])}</td>)}
                    <td style={tdS} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </Layout>
  );
}

const thS = { padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' };
const tdS = { padding: '10px 12px', fontSize: 13 };
